/**
 * Multi-Language Progress Utilities
 * 
 * Helper functions for managing progress across multiple languages
 * in a single profile.
 */

/**
 * Get language-specific progress from profile
 * @param {Object} profile - The profile object
 * @param {string} languageId - Language ID (e.g., 'Twi', 'Yoruba', 'French')
 * @returns {Object} Language-specific progress object
 */
export const getLanguageProgress = (profile, languageId) => {
  if (!profile || !languageId) return null;
  
  // If profile has new structure with progress[languageId]
  if (profile.progress && profile.progress[languageId]) {
    return profile.progress[languageId];
  }
  
  // Legacy: If profile has flat structure, return it for currentLanguage only
  if (profile.currentLanguage === languageId) {
    return {
      completedLessons: profile.completedLessons || [],
      completedUnits: profile.completedUnits || [],
      xp: profile.xp || 0,
      lastViewedUnit: profile.lastViewedUnit || 1,
      expandedUnits: profile.expandedUnits || [1, 2, 3],
      inProgressModule: profile.inProgressModule || null,
      hasCompletedStoryPreview: profile.hasCompletedStoryPreview || false,
      firstLessonCompleted: profile.firstLessonCompleted || false,
    };
  }
  
  // New language with no progress yet
  return {
    completedLessons: [],
    completedUnits: [],
    xp: 0,
    lastViewedUnit: 1,
    expandedUnits: [1, 2, 3],
    inProgressModule: null,
    hasCompletedStoryPreview: false,
    firstLessonCompleted: false,
  };
};

/**
 * Initialize progress structure for a new language
 * @param {string} languageId - Language ID to initialize
 * @returns {Object} Initial progress object for the language
 */
export const initializeLanguageProgress = (languageId) => {
  return {
    completedLessons: [],
    completedUnits: [],
    xp: 0,
    lastViewedUnit: 1,
    expandedUnits: [1, 2, 3],
    inProgressModule: null,
    hasCompletedStoryPreview: false,
    firstLessonCompleted: false,
  };
};

/**
 * Merge base profile with language-specific progress
 * Creates a "view" of the profile for a specific language
 * @param {Object} profile - Base profile object
 * @param {string} languageId - Language ID to merge
 * @returns {Object} Merged profile view
 */
export const mergeProfileWithLanguage = (profile, languageId) => {
  if (!profile) return null;
  
  const langProgress = getLanguageProgress(profile, languageId);
  
  // Merge base profile fields with language-specific progress
  return {
    ...profile,
    // Language-specific progress fields
    completedLessons: langProgress?.completedLessons || [],
    completedUnits: langProgress?.completedUnits || [],
    xp: langProgress?.xp || 0,
    lastViewedUnit: langProgress?.lastViewedUnit || 1,
    expandedUnits: langProgress?.expandedUnits || [1, 2, 3],
    inProgressModule: langProgress?.inProgressModule || null,
    hasCompletedStoryPreview: langProgress?.hasCompletedStoryPreview || false,
    firstLessonCompleted: langProgress?.firstLessonCompleted || false,
    // Keep currentLanguage for reference
    currentLanguage: languageId,
  };
};

/**
 * Calculate total XP across all languages
 * @param {Object} profile - Profile object with progress structure
 * @returns {number} Total XP
 */
export const calculateTotalXp = (profile) => {
  if (!profile) return 0;
  
  // New structure: sum XP from all languages
  if (profile.progress && typeof profile.progress === 'object') {
    return Object.values(profile.progress).reduce((total, langProgress) => {
      // Handle null/undefined/corrupted data gracefully
      if (!langProgress || typeof langProgress !== 'object') {
        return total;
      }
      // Ensure XP is a valid number
      const xp = typeof langProgress.xp === 'number' ? langProgress.xp : 0;
      return total + xp;
    }, 0);
  }
  
  // Legacy: single language profile
  return profile.xp || 0;
};

/**
 * Get list of languages with progress
 * @param {Object} profile - Profile object
 * @returns {Array<string>} Array of language IDs that have been started
 */
export const getActiveLanguages = (profile) => {
  if (!profile) return [];
  
  // New structure: get languages from progress object
  if (profile.progress && typeof profile.progress === 'object') {
    return Object.keys(profile.progress);
  }
  
  // Legacy: return currentLanguage if it exists
  if (profile.currentLanguage) {
    return [profile.currentLanguage];
  }
  
  return [];
};

/**
 * Check if a language has been started (has any progress)
 * @param {Object} profile - Profile object
 * @param {string} languageId - Language ID to check
 * @returns {boolean} True if language has been started
 */
export const hasLanguageStarted = (profile, languageId) => {
  const langProgress = getLanguageProgress(profile, languageId);
  if (!langProgress) return false;
  
  return (
    (langProgress.completedLessons?.length > 0) ||
    (langProgress.xp > 0) ||
    (langProgress.firstLessonCompleted === true)
  );
};

/**
 * List of fields that are language-specific (stored in progress[languageId])
 */
export const LANGUAGE_SPECIFIC_FIELDS = [
  'completedLessons',
  'completedUnits',
  'xp',
  'lastViewedUnit',
  'expandedUnits',
  'inProgressModule',
  'hasCompletedStoryPreview',
  'firstLessonCompleted'
];

/**
 * Check if a field is language-specific
 * @param {string} fieldName - Field name to check
 * @returns {boolean} True if field is language-specific
 */
export const isLanguageSpecificField = (fieldName) => {
  return LANGUAGE_SPECIFIC_FIELDS.includes(fieldName);
};

